import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["image", "placeholder", "filename", "dimensions", "filesize", "counter", "prevBtn", "nextBtn"]

  #assets = []
  #currentIndex = 0
  #preloadedImages = new Map() // Cache for preloaded images

  connect() {
    this.element.addEventListener("lightbox:open", this.#handleOpenEvent)
  }

  disconnect() {
    this.element.removeEventListener("lightbox:open", this.#handleOpenEvent)
    this.#removeKeyboardListeners()
  }

  #handleOpenEvent = (event) => {
    this.open(event)
  }

  #handleKeydown = (event) => {
    if (!this.element.open) return

    switch (event.key) {
      case "Escape":
        event.preventDefault()
        this.close()
        break
      case "ArrowLeft":
        event.preventDefault()
        this.previous()
        break
      case "ArrowRight":
        event.preventDefault()
        this.next()
        break
    }
  }

  #addKeyboardListeners() {
    document.addEventListener("keydown", this.#handleKeydown)
  }

  #removeKeyboardListeners() {
    document.removeEventListener("keydown", this.#handleKeydown)
  }

  open(event) {
    const { assets, index } = event.detail
    this.#assets = assets || []
    this.#currentIndex = index || 0

    if (this.#assets.length === 0) return

    this.#showCurrent()
    this.element.showModal()
    this.#addKeyboardListeners()
  }

  close() {
    this.#removeKeyboardListeners()
    this.element.close()
  }

  previous() {
    if (this.#currentIndex > 0) {
      this.#currentIndex--
      this.#showCurrent()
    }
  }

  next() {
    if (this.#currentIndex < this.#assets.length - 1) {
      this.#currentIndex++
      this.#showCurrent()
    }
  }

  #showCurrent() {
    const asset = this.#assets[this.#currentIndex]
    if (!asset) return

    const isImage = asset.type === "image"

    if (isImage && asset.url) {
      this.imageTarget.src = asset.url
      this.imageTarget.alt = asset.name
      this.imageTarget.classList.remove("hidden")
      this.placeholderTarget.classList.add("hidden")
      this.placeholderTarget.classList.remove("flex")
    } else {
      this.imageTarget.classList.add("hidden")
      this.placeholderTarget.classList.remove("hidden")
      this.placeholderTarget.classList.add("flex")
      this.#updatePlaceholderIcon(asset.type)
    }

    this.filenameTarget.textContent = asset.name || "-"
    this.dimensionsTarget.textContent = asset.dimensions || "-"
    this.filesizeTarget.textContent = asset.filesize || "-"
    this.counterTarget.textContent = `${this.#currentIndex + 1} / ${this.#assets.length}`

    this.prevBtnTarget.disabled = this.#currentIndex === 0
    this.nextBtnTarget.disabled = this.#currentIndex === this.#assets.length - 1

    // Preload adjacent images for smooth arrow key navigation
    this.#preloadAdjacentImages()
  }

  #preloadAdjacentImages() {
    // Preload previous image
    if (this.#currentIndex > 0) {
      this.#preloadImage(this.#assets[this.#currentIndex - 1])
    }

    // Preload next image
    if (this.#currentIndex < this.#assets.length - 1) {
      this.#preloadImage(this.#assets[this.#currentIndex + 1])
    }
  }

  #preloadImage(asset) {
    if (!asset || asset.type !== "image" || !asset.url) return
    if (this.#preloadedImages.has(asset.url)) return

    const img = new Image()
    img.src = asset.url
    this.#preloadedImages.set(asset.url, img)
  }

  #updatePlaceholderIcon(assetType) {
    const iconEl = this.placeholderTarget.querySelector("i")
    if (!iconEl) return

    const iconMap = {
      video: "fa-video",
      document: "fa-file-lines",
      archive: "fa-file-zipper",
      model: "fa-cube",
      audio: "fa-music"
    }

    const iconClass = iconMap[assetType] || "fa-file"
    iconEl.className = `fadr ${iconClass} text-6xl mb-4`
  }
}
